/**
 * 콜봇 월 정산 화면용 API 선택기 + 상태 있는 목 API + 개발용 조작.
 *
 * call-api 의 정산 라우트가 배포되기 전이라 화면을 목으로 먼저 만든다(요청 문서 "결론 먼저" 7). 목은 call-api
 * `feature/billing-settlement` 의 실제 응답 모양에 맞춰 두었다(월 정산 call-api 구현 회신) —
 * 배포 뒤에는 목만 끄면(`?settlementMock=0`) 실제 API 로 넘어간다.
 * **비운영 빌드(`NODE_ENV !== 'production'`)에서 `?settlementMock=1` 을 붙였을 때만** 목을 쓰고
 * (세션 동안 유지, `?settlementMock=0` 으로 끈다), 그 밖에는 항상 실제 settlementApi 다. 내선 관리의
 * `?pbxMock=1`(pbxProvisioningMock.js)과 같은 관례이고, 켜진 동안 화면 헤더에 `목 데이터` 배지를 보인다.
 * 서버가 배포되면 화면 코드를 바꾸지 않고 이 선택기가 실제 API 를 고른다.
 *
 * 요청 문서: 월 정산 프론트 요청 "요청 계약", 9월 예시 사용량 표
 * 목 원천: settlementMockData.js(9월 예시 사용량과 달별 사용량 — 병원 이름·회사 id 는 지어낸 값)
 * 계산·상태 전이: settlementMockEngine.js(call-api 흉내 — 이 파일은 지연·타이머만 씌운다)
 * 개발 조작 값 목록: settlementMockValues.js(마감 상태·건수 대사 장면)
 * 정산 시작일·엑셀 내보내기(2026-11-02 계약서 2절, call-api 요청서 3·9절): settlementMockApi 의 getSettlementStart ·
 *   previewSettlementStart · putSettlementStart · exportMonthlySettlement 와 개발 조작 get/setSettlementStartDate ·
 *   START_FAULT_OPTIONS · EXPORT_FAULT_OPTIONS · get/setStartFeatureMissing(옛 call-api) · get/setWebDaySplitMissing.
 *   처음은 모든 병원 시작일 미설정이라 지금 화면 그대로다.
 * 수납 장면(개발 조작, ~/md/front/admin-콜봇-월정산-카드결제-프론트요청.md 결론 3-8): settlementMockControls.PAYMENT_OPTIONS ·
 *   get/setPaymentScenario 로 병원·달마다 고르면 엔진이 settlementMockPayment.js 로 상세의 수납 칸 넷(계약 묶음 9.1)과 확정 고정
 *   409(9.2)를 만든다. get/setStopFailNextReopen 은 결제 요청 문서가 있는 장면의 다음 재수정 하나를 503 PAYMENT_STOP_FAILED 로(9.3).
 *
 * **운영 빌드에는 엔진·원천이 없다.** 원천에는 병원별 vox 원가·요율이 들어 있어 운영 번들에 싣지 않는다
 * ("vox 원가·마진은 병원에 절대 나가면 안 된다"). 그래서 엔진은 정적 import 가 아니라 아래
 * `if (process.env.NODE_ENV !== 'production') { … require('./settlementMockEngine') … }` 로만 불러온다.
 * 운영 빌드(build-prod)는 DefinePlugin 이 조건을 false 로 바꾸고 webpack 이 그 블록을 통째로 버려,
 * require 가 의존성으로 잡히지 않는다 — 엔진과 원천이 번들에 들어가지 않는다. 정적 import 였다면 목이 꺼져
 * 있어도 함께 실렸다. 이 파일이 정적으로 읽는 목 쪽 모듈은 값 목록(settlementMockValues.js)뿐이다.
 * build-dev·build-stage·serve 는 NODE_ENV 가 development 라 엔진·원천이 그대로 실린다(목을 쓸 수 있는 환경).
 * 공개 API 는 동기 그대로다. 운영 빌드에서 목 메서드·조작을 부르면 오류지만, 화면은 목이 켜졌을 때
 * (isSettlementMockEnabled() — 운영 빌드는 항상 false)만 부른다.
 *
 * 시그니처는 settlementApi 와 1:1 이다. 모든 메서드는 약 250ms 뒤 응답 본문의 깊은 복사본을 resolve
 * 하고, 실패는 axios 모양 오류({ isAxiosError, response: { status, data: { error } } })로 reject 한다.
 * 상태는 메모리에만 있다 — 새로고침하면 초기값으로 돌아간다.
 */
import { settlementApi } from '../api';
import { RECON_VALUES, STATUS_VALUES } from './settlementMockValues';
import { PAYMENT_VALUES } from './settlementMockValues';
import { EXPORT_FAULT_VALUES, START_FAULT_VALUES } from './settlementMockValues';

// 엔진은 비운영 빌드에만 싣는다(머리말). 운영 빌드에서는 null 이다.
let createServer = null;
if (process.env.NODE_ENV !== 'production') {
  createServer = require('./settlementMockEngine').createSettlementMockServer;
}

const MOCK_FLAG_KEY = 'admin.settlementMock';
/** 목 응답 지연 */
const MOCK_DELAY_MS = 250;
/** 수동 마감 실행이 끝나기까지(비동기 202 흉내) */
const CLOSE_RUN_MS = 1500;
/** 정산 내보내기 응답 지연 — 회사 여러 곳의 상세를 만드는 흉내(버튼 로딩이 보이게) */
const EXPORT_DELAY_MS = 600;

/** 비운영 빌드 + `?settlementMock=1`(세션 유지) 일 때만 true */
export function isSettlementMockEnabled() {
  if (process.env.NODE_ENV === 'production') return false;
  try {
    const q = new URLSearchParams(window.location.search).get('settlementMock');
    if (q === '1') sessionStorage.setItem(MOCK_FLAG_KEY, '1');
    if (q === '0') sessionStorage.removeItem(MOCK_FLAG_KEY);
    return sessionStorage.getItem(MOCK_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/** 월 정산 화면은 항상 이걸로 API 를 얻는다(페이지 setup 에서 한 번) */
export function getSettlementApi() {
  return isSettlementMockEnabled() ? settlementMockApi : settlementApi;
}

/**
 * 목 켜는 법 안내 — 운영 빌드에는 목이 없어 빈 문자열이다(운영 화면에 개발용 스위치를 알리지 않는다).
 * 목록·상세·기본 요율·마감 요약이 같은 문구를 쓴다.
 */
export const SETTLEMENT_MOCK_HINT =
  process.env.NODE_ENV !== 'production' ? '개발 환경에서는 주소에 ?settlementMock=1 을 붙이면 목 데이터로 볼 수 있습니다.' : '';

/** 이 환경의 call-api 에 정산 라우트가 없을 때(404·501)의 안내 — 목록·상세가 쓴다 */
export const SETTLEMENT_UNIMPLEMENTED_TEXT = ['이 환경의 call-api 에 월 정산 API 가 아직 없습니다(배포 전).', SETTLEMENT_MOCK_HINT]
  .filter(Boolean)
  .join(' ');

// ---------------------------------------------------------------------------
// 목 구현 — 엔진(동기)에 지연을 씌운다. 상태는 지연 뒤에 읽어 앞선 쓰기가 반영된다.
// ---------------------------------------------------------------------------

// 목을 처음 쓸 때 만든다 — 목이 꺼진 화면은 9월 원천을 계산하지 않는다. 운영 빌드에는 엔진이 없다.
let mockServer = null;
function srv() {
  if (!createServer) throw new Error('월 정산 목은 운영 빌드에 없습니다.');
  if (!mockServer) mockServer = createServer();
  return mockServer;
}

function run(fn, ms = MOCK_DELAY_MS) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        resolve(fn());
      } catch (err) {
        reject(err);
      }
    }, ms);
  });
}

/** settlementApi 와 같은 이름·인자 */
export const settlementMockApi = {
  listMonthlyUsage: (month) => run(() => srv().api.listMonthlyUsage(month)),
  getSettlement: (companyId, month) => run(() => srv().api.getSettlement(companyId, month)),
  listDefaultPlans: () => run(() => srv().api.listDefaultPlans()),
  putDefaultPlan: (effectiveFrom, body) => run(() => srv().api.putDefaultPlan(effectiveFrom, body)),
  deleteDefaultPlan: (effectiveFrom) => run(() => srv().api.deleteDefaultPlan(effectiveFrom)),
  listPlanOverrides: (companyId) => run(() => srv().api.listPlanOverrides(companyId)),
  putPlanOverride: (companyId, effectiveFrom, body) => run(() => srv().api.putPlanOverride(companyId, effectiveFrom, body)),
  deletePlanOverride: (companyId, effectiveFrom) => run(() => srv().api.deletePlanOverride(companyId, effectiveFrom)),
  getCloseRuns: (month) => run(() => srv().api.getCloseRuns(month)),
  // 202 { run_id } 를 바로 주고, 계산은 잠시 뒤 끝난다 — 결과는 새로고침으로 본다
  startCloseRun: ({ month, companyId } = {}) =>
    run(() => {
      const res = srv().api.startCloseRun({ month, companyId });
      setTimeout(() => srv().finishCloseRun(res.run_id), CLOSE_RUN_MS);
      return res;
    }),
  saveStatement: (companyId, month, body) => run(() => srv().api.saveStatement(companyId, month, body)),
  recalculateStatement: (companyId, month) => run(() => srv().api.recalculateStatement(companyId, month)),
  confirmStatement: (companyId, month, body) => run(() => srv().api.confirmStatement(companyId, month, body)),
  reopenStatement: (companyId, month, body) => run(() => srv().api.reopenStatement(companyId, month, body)),
  listOneOffItems: (companyId, month) => run(() => srv().api.listOneOffItems(companyId, month)),
  createOneOffItem: (companyId, body) => run(() => srv().api.createOneOffItem(companyId, body)),
  updateOneOffItem: (companyId, itemId, body) => run(() => srv().api.updateOneOffItem(companyId, itemId, body)),
  deleteOneOffItem: (companyId, itemId) => run(() => srv().api.deleteOneOffItem(companyId, itemId)),
  getReconciliation: (companyId, month, { missingIds = false } = {}) =>
    run(() => srv().api.getReconciliation(companyId, month, { missingIds })),
  getMonthlyStats: (companyId, month) => run(() => srv().api.getMonthlyStats(companyId, month)),
  getPbxCall: (companyId, voxCallId) => run(() => srv().api.getPbxCall(companyId, voxCallId)),
  // 정산 시작일·엑셀 내보내기(계약서 2절) — opts(timeout)는 목에서 쓰지 않는다(시간 초과는 개발 조작 '시간 초과' 장면)
  getSettlementStart: (companyId) => run(() => srv().api.getSettlementStart(companyId)),
  previewSettlementStart: (companyId, startDate) => run(() => srv().api.previewSettlementStart(companyId, startDate)),
  putSettlementStart: (companyId, body) => run(() => srv().api.putSettlementStart(companyId, body)),
  exportMonthlySettlement: (body, opts = {}) => run(() => srv().api.exportMonthlySettlement(body, opts), EXPORT_DELAY_MS),
};

// ---------------------------------------------------------------------------
// 개발용 조작 — 목이 켜졌을 때만 화면에 보인다(동기 호출, 바꾼 뒤 화면이 다시 불러온다)
// ---------------------------------------------------------------------------

const STATUS_LABELS = {
  estimating: '추정 중',
  closing: '마감 중',
  review: '검토 필요',
  review_reopened: '검토 필요(재수정)',
  confirmed_system: '확정(자동)',
  confirmed_human: '확정(사람)',
  failed: '마감 실패',
};

const RECON_LABELS = {
  match: '일치',
  mismatch: '불일치',
  too_many_calls: '불일치 · 누락 ID 너무 많음',
  rate_limited: 'vox 429(마감은 실패)',
  not_provisioned: '하위 조직 없음 409(웹 미확인)',
  web_voice_misread: '일치 · 웹 텍스트를 음성으로 나눔(예전 규칙 — 웹 음성 원가 불일치)',
};

/** 수납 장면(settlementMockValues.js PAYMENT_VALUES — 뜻은 그 머리말) */
const PAYMENT_LABELS = {
  off: '결제 꺼짐(수납 상태 없음)',
  before_start: '수납 시작 월 이전',
  in_no_doc: '결제 걸린 달 · 요청 문서 없음',
  awaiting_deposit: '입금 대기(계좌이체)',
  overdue: '입금 대기 · 기한 지남',
  paid: '입금 받음(고른 때 목표 전액)',
  pw_pending: 'PW 반영 전(반영 중)',
  processing: '결제 중(카드)',
  scheduled: '승인됨 · 처리 대기(카드)',
  held_invalid: '보류 INVALID_STATEMENT',
  state_unreadable: '결제 상태 읽기 실패(재수정 503)',
  ledger_unreadable: 'PW 장부 읽기 실패',
};

/** 다음 정산 시작일 저장 하나의 장면(settlementMockValues.js START_FAULT_VALUES — 뜻은 그 머리말) */
const START_FAULT_LABELS = {
  none: '없음',
  rev_changed: '409 다른 사람이 먼저 바꿈(rev)',
  audit_unavailable: '503 이용 내역 기록 실패(되돌림)',
  discard_conflict: '저장됨 · 지울 정산서가 그사이 바뀌어 남음',
  confirmed_during_change: '저장됨 · 그사이 확정된 정산서가 남음',
};

/** 다음 정산 내보내기 하나의 장면(settlementMockValues.js EXPORT_FAULT_VALUES) */
const EXPORT_FAULT_LABELS = {
  none: '없음',
  audit_unavailable: '503 이용 내역 기록 실패',
  perm_unavailable: '503 권한 확인 실패',
  support_session_ended: '401 지원 세션 끝남',
  permission_denied: '403 권한 없음',
  unknown_company: '400 이 달 목록에 없는 병원',
  export_failed: '500 일부 병원 계산 실패',
  timeout: '시간 초과(응답 없음)',
  not_found: '404 경로 없음(배포 전 call-api)',
};

export const settlementMockControls = {
  /** 마감 상태 다섯 가지(확정은 자동·사람, 검토 필요는 재수정 포함) */
  STATUS_OPTIONS: Object.freeze(STATUS_VALUES.map((value) => Object.freeze({ value, label: STATUS_LABELS[value] }))),
  /** 건수 대사([vox 건수 확인]·자동 마감의 vox 건수 조회) 결과 */
  RECON_OPTIONS: Object.freeze(RECON_VALUES.map((value) => Object.freeze({ value, label: RECON_LABELS[value] }))),
  getStatus: (companyId, month) => srv().controls.getStatus(companyId, month),
  setStatus: (companyId, month, value) => srv().controls.setStatus(companyId, month, value),
  getReconScenario: (companyId, month) => srv().controls.getReconScenario(companyId, month),
  setReconScenario: (companyId, month, value) => srv().controls.setReconScenario(companyId, month, value),
  getAutoCloseEnabled: () => srv().controls.getAutoCloseEnabled(),
  setAutoCloseEnabled: (enabled) => srv().controls.setAutoCloseEnabled(enabled),
  /**
   * 삭제된 회사로 표시한다 — 목록·마감 현황에 "삭제된 회사"로 나온다(회신 표 6). 상세 등 회사 경로 라우트는
   * 삭제된 회사도 받는다(회신 "2026-10-19 개정").
   */
  getDeletedCompanies: () => srv().controls.getDeletedCompanies(),
  setCompanyDeleted: (companyId, deleted) => srv().controls.setCompanyDeleted(companyId, deleted),
  /** 다음 정산 쓰기(저장·다시 계산·확정·재수정·일회성 항목) 하나를 409 STATEMENT_CONFLICT 로 만든다(회신 표 10) */
  getConflictNextWrite: () => srv().controls.getConflictNextWrite(),
  setConflictNextWrite: (on) => srv().controls.setConflictNextWrite(on),
  /** 정산 인덱스 미준비 — [지금 마감 실행]·[지금 다시 실행]이 503 BILLING_INDEXES_NOT_READY(회신 표 11) */
  getIndexesNotReady: () => srv().controls.getIndexesNotReady(),
  setIndexesNotReady: (on) => srv().controls.setIndexesNotReady(on),
  /**
   * 수납 장면(병원·달마다, 기본 off) — 상세의 payment_scope·payment·payment_error·payment_confirm_preview(계약 묶음 9.1)와
   * 확정의 expected_rev 검사(9.2)가 바뀐다. 'paid' 는 고른 때의 목표 합계를 받은 순액으로 고정한다
   */
  PAYMENT_OPTIONS: Object.freeze(PAYMENT_VALUES.map((value) => Object.freeze({ value, label: PAYMENT_LABELS[value] }))),
  getPaymentScenario: (companyId, month) => srv().controls.getPaymentScenario(companyId, month),
  setPaymentScenario: (companyId, month, value) => srv().controls.setPaymentScenario(companyId, month, value),
  /** 다음 재수정 하나를 503 PAYMENT_STOP_FAILED 로(9.3) — 결제 요청 문서가 있는 장면에서만 걸리고, 한 번 쓰이면 꺼진다 */
  getStopFailNextReopen: () => srv().controls.getStopFailNextReopen(),
  setStopFailNextReopen: (on) => srv().controls.setStopFailNextReopen(on),
  /**
   * 그 달 미종료 통화를 끝낸다(사용량의 not_final → 0). 추정 중 달은 바로, 검토 필요 달은 [다시 계산] 뒤
   * NOT_FINAL_CALLS 가 풀리고(상태는 검토 필요 그대로), 확정 달은 STATEMENT_DRIFT 가 붙는다.
   * @returns {boolean} 바꿨으면 true — 그 달 사용량이 없거나 미종료 통화가 없으면 false
   */
  finishOpenCalls: (companyId, month) => srv().controls.finishOpenCalls(companyId, month),
  /**
   * 늦게 들어온 통화 n건(1분짜리 AI 응대)을 그 달 사용량에 더한다. 확정 달은 금액은 그대로이고
   * STATEMENT_DRIFT(비차단)가 붙는다. 추정 중 달은 바로, 검토 필요 달은 [다시 계산] 뒤 반영된다.
   * 마감 때 읽어 둔 vox 건수보다 DB 가 n건 많아지므로, 그 건수로 다시 계산하면 COUNT_MISMATCH 가 붙는다
   * ([vox 건수 확인]으로 다시 읽고 다시 계산하면 풀린다).
   * @returns {boolean} 바꿨으면 true — 그 달 사용량이 없으면 false
   */
  addLateCalls: (companyId, month, n = 2) => srv().controls.addLateCalls(companyId, month, n),
  /**
   * 그 적용 시작 월의 기본 요율 버전을 요금표 v70 이전 모양으로 바꾼다(없으면 만든다) — 배포 직후 intest 의 2026-09 처럼
   * `legacy: true` 로 보이고, 그 버전이 적용되는 달은 DEFAULT_PLAN_MISSING(`legacy_version`)이다. 새 칸으로 다시 저장하면
   * 풀린다(요금표 v70 개정 1·7).
   * @returns {boolean} 바꿨으면 true
   */
  setLegacyDefaultPlan: (effectiveFrom) => srv().controls.setLegacyDefaultPlan(effectiveFrom),
  /**
   * 결제 채널의 수납 시작 월('YYYY-MM' | null) — 기본 요율 목록의 상담사 번호 요금 시작 월 경고(`fee_start_warning`)를 바꿔 볼 때
   * 쓴다. 처음은 없음이라 상담사 번호 요금 버전(2026-10)이 있으면 경고가 뜬다. 바꾼 뒤 목록을 다시 부른다
   */
  getPaymentFromMonth: () => srv().controls.getPaymentFromMonth(),
  setPaymentFromMonth: (month) => srv().controls.setPaymentFromMonth(month),
  /**
   * 정산 시작일을 규칙 검사 없이 바로 바꾼다('YYYY-MM-DD' | null) — 정산서를 지우지 않아 시작 전 달에 "남은 정산서" 장면을 만든다.
   * @returns {boolean} 바꿨으면 true(형식이 틀리거나 같은 값이면 false)
   */
  getSettlementStartDate: (companyId) => srv().controls.getSettlementStartDate(companyId),
  setSettlementStartDate: (companyId, dateKey) => srv().controls.setSettlementStartDate(companyId, dateKey),
  /** 다음 정산 시작일 저장(PUT) 하나의 오류 장면 — 한 번 쓰이면 없음 */
  START_FAULT_OPTIONS: Object.freeze(START_FAULT_VALUES.map((value) => Object.freeze({ value, label: START_FAULT_LABELS[value] }))),
  getStartPutFault: () => srv().controls.getStartPutFault(),
  setStartPutFault: (value) => srv().controls.setStartPutFault(value),
  /** 다음 정산 시작일 미리보기(GET preview) 하나를 500 으로 — 한 번 쓰이면 꺼진다 */
  getStartPreviewFault: () => srv().controls.getStartPreviewFault(),
  setStartPreviewFault: (on) => srv().controls.setStartPreviewFault(on),
  /** 다음 정산 내보내기 하나의 오류 장면 — 한 번 쓰이면 없음 */
  EXPORT_FAULT_OPTIONS: Object.freeze(EXPORT_FAULT_VALUES.map((value) => Object.freeze({ value, label: EXPORT_FAULT_LABELS[value] }))),
  getExportFault: () => srv().controls.getExportFault(),
  setExportFault: (value) => srv().controls.setExportFault(value),
  /** 옛 call-api 흉내 — 새 칸이 모두 빠지고 시작일·내보내기 경로 넷이 404(배포 전 안내) */
  getStartFeatureMissing: () => srv().controls.getStartFeatureMissing(),
  setStartFeatureMissing: (on) => srv().controls.setStartFeatureMissing(on),
  /** 웹 일별 칸 없음(그 병원·달) — 시작 월이고 웹이 있으면 WEB_START_SPLIT_UNAVAILABLE, [vox 건수 확인]으로 풀린다 */
  getWebDaySplitMissing: (companyId, month) => srv().controls.getWebDaySplitMissing(companyId, month),
  setWebDaySplitMissing: (companyId, month, on) => srv().controls.setWebDaySplitMissing(companyId, month, on),
  /** 처음 상태로(9월 마감 결과·시드 요율·예외·일회성 항목·사용량) */
  reset: () => srv().controls.reset(),
};

/**
 * 연결 진단의 "통화 1건 연결 확인"에 넣어 볼 통화 ID(목 전용 — 응답 견본과 미응답 몇 건).
 * 미응답 ID 는 리포트 응답에도 있지만, 응답한 통화 ID 는 목 밖에서 알 길이 없어 따로 준다.
 * @returns {{ answered: string[], unanswered: string[] }}
 */
export function settlementMockSampleCallIds(companyId, month) {
  return srv().sampleCallIds(companyId, month);
}
